import { useEffect, useMemo, useState } from "react";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { Identifier } from "@shared/services/api/types";
import type { DateInput } from "@shared/utils/dates/formatting";
import { parseDate } from "@shared/utils/dates/parsing";

// display when there's <= this many seconds left before basket expires
const DURATION_TO_DISPLAY = 120;

export const useAllowAddMoreCartTime = (
  targetDate: DateInput,
  paymentVenue: number
): boolean => {
  // fetch the current venue to get venue.enable_customer_basket_extend
  const { useRetrieve } = usePublicVenueService();
  const { data: venueData } = useRetrieve(paymentVenue as Identifier, {
    options: { enabled: Boolean(paymentVenue) },
  });

  const canExtendBasket = useMemo(
    () => Boolean(venueData?.enable_customer_basket_extend),
    [venueData?.enable_customer_basket_extend]
  );

  const [allowAddMoreTime, setAllowAddMoreTime] = useState(false);

  useEffect(() => {
    const checkTime = (): ReturnType<typeof setTimeout> | null => {
      const now = Date.now();
      const expiry = targetDate == null ? new Date() : parseDate(targetDate);
      const remainingTime = expiry.getTime() - now;

      // Only allow extending if we're within the display duration and can extend
      const shouldAllow =
        remainingTime <= DURATION_TO_DISPLAY * 1000 && canExtendBasket;
      setAllowAddMoreTime(shouldAllow);

      // If we're not yet within display duration, set up the next check
      if (remainingTime > DURATION_TO_DISPLAY * 1000) {
        const timeUntilDisplay = remainingTime - DURATION_TO_DISPLAY * 1000;
        return setTimeout(() => checkTime(), timeUntilDisplay);
      }

      return null;
    };

    const timeoutId = checkTime();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [targetDate, canExtendBasket]);

  return allowAddMoreTime;
};
